<template>
  <div>
      <div class="industry-sub-wrapper">
        <div style="flex-basis: 50%" class="relative" id="industry">
          <v-select
            v-model="formData.industry"
            :items="industries"
            :rules="validations.industry"
            append-icon="mdi-chevron-down"
            label="Industry"
            filled
            @change="handleIndustryChange"
            >
            <template v-slot:item="data">
              <p class="v-list-item__title" :id="data.item">{{ data.item }}</p>
            </template>
          </v-select>
        </div>
        <div style="flex-basis: 50%" class="relative" id="sub-industry">
          <v-select
            v-model="formData.sub_industry"
            :items="subIndustries"
            @change="handleSubIndustryChange"
            :rules="formData.industry ? validations.sub_industry : '' "
            append-icon="mdi-chevron-down"
            label="Sub Industry"
            filled
            :disabled="!formData.industry"
            :menu-props="{ contentClass: 'selectScroll'}"
            >
            <template v-slot:item="data">
              <p class="v-list-item__title" :id="data.item">{{ data.item }}</p>
            </template>
          </v-select>
        </div>
      </div>
      <div class="industry-sub-wrapper">
        <div v-if="!shouldHideBusinessName" class="business-name-wrapper">
          <v-text-field
            dense
            filled
            rounded
            :placeholder="getPlaceHolder"
            v-model="formData.businessName"
            :rules="validations.businessNameRules"
            :error-messages="formFieldsErrors.businessName"
            v-on:focus="clearFieldError('businessName')"
            type="text"
            validate-on-blur
            id="businessName"
          >
            <template slot="label">
              Legal Business Name
            </template>
            <template v-slot:message="{ message, key }">
              <span :key="key" id="businessMessage" v-html="message"></span>
            </template>
          </v-text-field>
        </div>

        <div v-if="!shouldHideEinAndState" class="industry-sub-wrapper">

          <div class="relative ein-wrapper">

            <div v-if="flags.isEinToolTipShown" class="ein-tt fade-in -helper--flex column relative">
              <h1>What is an EIN number?</h1>
              <p>Employer Identification Number, that is used to identify business entities for tax purposes. Also known as the Federal Tax Identification Number (TIN).</p>
              <span class="edge" />
            </div>

            <v-text-field
              dense
              filled
              rounded
              v-model="formData.ein"
              :rules="validations.einRules"
              :error-messages="formFieldsErrors.ein"
              v-on:focus="clearFieldError('ein')"
              v-mask="'##-#######'"
              type="tel"
              validate-on-blur
              id="ein"
              ref="elEin"
            >
              <template slot="label">
                EIN Number
              </template>
              <template v-slot:message="{ message, key }">
                <span :key="key" id="einMessage" v-html="message"></span>
              </template>
            </v-text-field>
            <img style="cursor: pointer" @mouseleave="flags.isEinToolTipShown = false" @click="handleEinDisclaimer" class="einIcon" src="../../../RF_assets/icons/ein-info.svg" alt="">
          </div>

          <div v-if="!shouldHideEinAndState" class="relative registered-state" id="">
            <v-select
              v-model="formData.registeredState"
              :items="states"
              :rules="!shouldHideEinAndState ? validations.registeredState : '' "
              validate-on-blur
              item-text="title"
              item-value="id"
              append-icon="mdi-chevron-down"
              filled
              v-on:focus="clearFieldError('registeredState')"
              :error-messages="formFieldsErrors.registeredState"
              :menu-props="{ contentClass: 'selectScroll'}"
              >
                <template v-slot:item="data">
                  <p class="v-list-item__title" :id="data.item.id">{{ data.item.title }}</p>
                </template>
                <template v-slot:label>
                  <p class="input-legend" style="font-size: 14px;">{{ customerData.incomeType === 'GENERAL_PARTNERSHIP' ? 'Formation state' : 'Registered state' }}</p>
                </template>
                <template v-slot:message="{ message, key }">
                  <span :key="key" id="registeredStateMessage" v-html="message"></span>
                </template>
            </v-select>
          </div>

        </div>
      </div>

    <div v-if="shouldShowDbaDisclaimer" class="dba-disclaimer -helper--flex column">
      <p>In order to approve your business name (DBA) you will need to add your DBA documentation in the Lili app once your account is opened.
      </p>
      <div class="divider"></div>
      <p class="heading">Please get the following documents ready:</p>
      <p>Business name (DBA) Registration or Fictitious Name Registration or Assumed Name Certificate
      </p>
    </div>

    <p class="listed-activities">Note, Lili cannot provide services to businesses that are involved in or
      associated with any of the <span @click="handleListedActivities">listed activities</span></p>

    <div
      ref="unsupportedToolTip"
      v-if="flags.isToolTipShown"
      class="unsupported-tt fade-in -helper--flex column"
    >
      <div v-for="item in toolTipData" class="tt-item -helper--flex align-center">
        <img style="margin-right: 12px; width: 24px" :src=dynamicImportIcon(item.imgUrl) width="22px"/>
        <p class="tt-item-title">{{ item.title }}</p>
      </div>
      <span @click.stop="() => flags.isToolTipShown = false" class="close-popup-icon"/>
    </div>

  </div>
</template>

<script>
import {params} from '../../../localData'
import {mdiInformation} from '@mdi/js'

export default {
  name: 'BusinessDetailsForm',
  async mounted () {
    await this.getIndustryList()
  },
  props: ['hiddenFields'],
  watch: {
    formData: {
      handler (newVal) {
        this.$store.commit('customer/setKeyValue', {prop: 'segment', value: newVal.industry})
        this.$store.commit('customer/setKeyValue', {prop: 'sub_industry', value: newVal.sub_industry})
        this.$store.commit('customer/setKeyValue', {prop: 'businessName', value: newVal.businessName})
        this.$store.commit('customer/setKeyValue', {prop: 'businessRegisteredState', value: newVal.registeredState})
        this.$store.commit('customer/setKeyValue', {prop: 'ein', value: newVal.ein})
      },
      deep: true
    }
  },
  beforeMount () {
    this.formData = {
      industry: this.customerData.segment || '',
      sub_industry: this.customerData.sub_industry || '',
      businessName: this.customerData.businessName || '',
      ein: this.customerData.ein || '',
      registeredState: this.customerData.businessRegisteredState || ''
    }
  },
  methods: {
    handleSubIndustryChange () {
      this.$store.commit('customer/setKeyValue', {prop: 'sub_industry', value: this.formData.sub_industry})
    },
    handleIndustryChange () {
      this.formData.sub_industry = ''
      this.formErrors.sub_industry = false
    },
    handleEinDisclaimer () {
      this.flags.isEinToolTipShown = !this.flags.isEinToolTipShown
    },
    async getIndustryList () {
      try {
        const res = await this.$store.dispatch(
          'customer/updateCustomer',
          {
            ...this.ajaxData,
            header: {
              headerName: params.httpHeaders.auth,
              headerValue: 'lili ' + this.customerData.accessToken
            }
          },
          this
        )
        this.industryList = res.industryList
      } catch (e) {
        console.log(e)
      }
    },
    clearFieldError (field) {
      this.$store.dispatch('errors/clearSpecificFieldError', [
        'defaultError',
        field
      ])
    },
    handleListedActivities () {
      this.flags.isToolTipShown = !this.flags.isToolTipShown
    },
    dynamicImportIcon (iconName) {
      return require(`../../../RF_assets/icons/${iconName}.svg`)
    }
  },
  computed: {
    customerData () {
      return this.$store.getters['customer/data']
    },
    shouldHideEinAndState () {
      return this.customerData.incomeType.enum === 'SSN' || this.customerData.incomeType.enum === 'DBA'
    },
    shouldHideBusinessName () {
      return this.customerData.incomeType.enum === 'SSN'
    },
    shouldShowDbaDisclaimer () {
      return this.customerData.incomeType.enum === 'DBA'
    },
    ajaxData () {
      return {
        method: 'get',
        endPoint: params.restEndpoints.getIndustry.replace(
          '{customerid}',
          this.customerData.customerId
        ),
        self: this
      }
    },
    industries () {
      return this.industryList.map(i => i.industry)
    },
    subIndustries () {
      if (!this.formData.industry) return []
      const industryIndex = this.industryList.findIndex(i => i.industry === this.formData.industry)
      if (industryIndex > -1) {
        return this.industryList[industryIndex].subIndustryList
      }
      return []
    },
    getPlaceHolder () {
      const PLACEHOLDER_MAP = {
        S_CORP: 'Example: Better Together Inc',
        MMLLC: 'Example: The Flower Shop LLC',
        LLC: 'Example: The New Wave LLC',
        LLP: 'Example: Next Step Consulting LLP',
        GENERAL_PARTNERSHIP: 'Example: Bright Design Partnership'
      }
      return PLACEHOLDER_MAP[this.customerData.incomeType]
    },
    formFieldsErrors () {
      return this.$store.getters['errors/fieldErrors']
    },
    states () {
      return params.states
    }
  },
  data: () => {
    return {
      icons: {
        info: mdiInformation
      },
      flags: {
        isEinToolTipShown: false,
        isToolTipShown: false
      },
      formErrors: {
        industry: false,
        sub_industry: false
      },
      validations: {
        businessNameRules: [
          v => !!v || 'Business name is required.',
          v => (v && v.length >= 3) || 'Business name is too short',
          v => (v && /(.*[a-z]){3}/i.test(v)) || 'Business name is invalid',
          v => (v && v.length < 100) || 'Business name is too long'
        ],
        einRules: [
          v => !!v || 'EIN number is required.',
          v => (v && v.length >= 10) || 'Invalid EIN number'
        ],
        industry: [v => !!v || 'Industry is required'],
        sub_industry: [v => !!v || 'Sub industry is required'],
        registeredState: [v => !!v || 'State is required']
      },
      formData: {
        industry: '',
        sub_industry: '',
        businessName: '',
        ein: '',
        registeredState: ''
      },
      industryList: [],
      toolTipData: [
        {
          imgUrl: 'Cryptocurrencies',
          title: 'Cryptocurrencies'
        },
        {
          imgUrl: 'moneyservices',
          title: 'Money services'
        },
        {
          imgUrl: 'Gambling',
          title: 'Gambling'
        },
        {
          imgUrl: 'Crowdfunding',
          title: 'Crowdfunding'
        },
        {
          imgUrl: 'Trust',
          title: 'Trust'
        }, {
          imgUrl: 'nonprofit',
          title: 'Non profit, Charity and Foundation'
        }, {
          imgUrl: 'Marijuana',
          title: 'Marijuana/Cannabis'
        }, {
          imgUrl: 'ATM',
          title: 'Privately-owned ATMs'
        },
        {
          imgUrl: '18-plus',
          title: 'Adult Entertainment'
        },
        {
          imgUrl: 'firearm-manufacturing',
          title: 'Firearm Manufacturing'
        }
      ]
    }
  }
}
</script>

<style lang="css" scoped>
.industry-sub-wrapper {
  display: flex;
  align-items: start;
  gap: 24px;

  #elSelectIndustry {
    max-width: 100% !important;
  }
}

.business-name-wrapper {
  width: 330px;
  min-width: 330px;
}

.listed-activities {
  font-family: MetricRRegular;
  font-size: 14px;
  color: #525D71;
  line-height: 20px;
  margin-bottom: 16px;
}

.listed-activities span {
  color: #0078A7;
  cursor: pointer;
  font-family: MetricRMedium;
  font-size: 14px;
  text-decoration: underline;
}

.select-error {
  color: #e75858;
  font-family: SourceSansPro-Regular;
  font-size: 12px;
  position: absolute;
  left: 0;
  top: 0;
  transform: translateY(58px);
}

.ein-wrapper {
  width: 153px;
}

.registered-state {
  width: 153px;
}

.einIcon {
  position: absolute;
  right: 0;
  top: 0;
  width: 20px;
  transform: translate(-10px, 17px);
}

@media only screen and (max-width: 480px) {
  .industry-sub-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 0px;
  }

  .industry-sub-wrapper > div {
    width: 100%;
  }

  .dba-disclaimer {
    margin-top: 24px;
  }

  .unsupported-tt {
    width: 300px;
    max-width: 100%;
    padding: 16px;
    position: absolute;
    top: unset;
    left: -330px;
    z-index: 999;
  }

  .unsupported-tt .tt-item {
    margin-bottom: 8px;
  }

  .unsupported-tt .close-popup-icon {
    cursor: pointer;
    position: absolute;
    top: 12px;
    right: 12px;
    width: 12px;
    height: 12px;
  }

  .ein-wrapper {
    width: 100%;
  }

  .ein-icon {
    width: 23px;
  }

  .ein-tt .edge {
    transform: translate(145px, -23px) rotate(45deg);
  }

  .listed-activities {
    margin-top: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/PartnerOnboarding/business-sections/BusinessDetailsForm.vue